/**
 * Centralized deep-link builders. One place that knows the canonical URL shape
 * for each cross-tab / external link, so inline literals don't drift
 * (docs/specs/architecture/navigation-deeplink-spec.md §7).
 *
 * `intent` / `return_to` are the navigation-context params (§3.6): `intent` is a
 * one-shot arrival hint (consumed once, then stripped by the destination);
 * `return_to` powers in-app "Back to X" affordances.
 */

/** Navigation-context options carried by a deep link. */
export interface NavCtx {
  intent?: string;
  returnTo?: string;
}

function withCtx(base: string, ctx: NavCtx = {}): string {
  const u = new URLSearchParams();
  if (ctx.intent) u.set('intent', ctx.intent);
  if (ctx.returnTo) u.set('return_to', ctx.returnTo);
  const q = u.toString();
  if (!q) return base;
  return `${base}${base.includes('?') ? '&' : '?'}${q}`;
}

export const deepLinks = {
  /** Deal Updates card. Matches the live convention `/deals?deal_key=…&intent=…&return_to=…`. */
  deal: (key: string, ctx?: NavCtx): string =>
    withCtx(`/deals?deal_key=${encodeURIComponent(key)}`, ctx),

  /** Pipeline Insights, investigate mode, focused on a deal. */
  insightsForDeal: (kind: string, key: string, ctx?: NavCtx): string =>
    withCtx(
      `/pipeline-insights?mode=investigate&deal_kind=${encodeURIComponent(kind)}&deal_key=${encodeURIComponent(key)}`,
      ctx,
    ),

  /** Pipeline Insights, investigate mode, focused on a signal. */
  insightsForSignal: (signalId: number, ctx?: NavCtx): string =>
    withCtx(`/pipeline-insights?mode=investigate&signal=${signalId}`, ctx),

  /**
   * The CRM record panel, open over its parent list's grid — `/crm?sel=list:X&rec=Y`.
   *
   * The review surface for a deal's pending AI field updates (badge spec §1). Deliberately carries
   * NO `intent`: the arrival-hint machinery dies with the Deals tab, and the panel needs no hint
   * because the proposals are visible in place. Mirrors the email-side builder
   * `dashboard_urls.record_deeplink`.
   */
  crmRecord: (listId: number, entryId: number, ctx?: NavCtx): string =>
    withCtx(`/crm?sel=${encodeURIComponent(`list:${listId}`)}&rec=${entryId}`, ctx),

  /** Governed CRM profile. The destination independently checks the caller's entitlement. */
  crmEntity: (kind: 'person' | 'organization', id: number, ctx?: NavCtx): string =>
    withCtx(`/crm?sel=${encodeURIComponent(`${kind === 'organization' ? 'org' : 'person'}:${id}`)}`, ctx),

  /** Governed full-message view. No subject/body travels in the URL or the source report. */
  crmMessage: (id: number, ctx?: NavCtx): string =>
    withCtx(`/crm?sel=${encodeURIComponent(`msg:${id}`)}`, ctx),

  /** Outreach campaign detail. */
  campaign: (id: number, ctx?: NavCtx): string =>
    withCtx(`/outreach?section=campaigns&campaign=${id}`, ctx),

  /** NDA review detail. */
  ndaReview: (id: number, ctx?: NavCtx): string => withCtx(`/nda?review=${id}`, ctx),
};
